import { FC, useMemo, useState, useCallback } from 'react';
import { Button, Modal } from 'react-daisyui';
import { ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { InProgressAttemptWithPaper } from '@/api/useInProgressPaperAttempt';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import useDeletePaperAttempt from '@/api/useDeletePaperAttempt';

interface InProgressBannerProps {
  attempt: InProgressAttemptWithPaper;
}

const InProgressBanner: FC<InProgressBannerProps> = ({ attempt }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deletePaperAttempt = useDeletePaperAttempt();
  const [showExitModal, setShowExitModal] = useState(false);

  // Calculate time remaining
  const timeRemaining = useMemo(() => {
    const startedAt = new Date(attempt.started_at).getTime();
    const timeAllowedMs = (attempt.past_papers.time_allowed || 0) * 60 * 1000;
    const now = Date.now();
    const remainingMs = startedAt + timeAllowedMs - now;

    if (remainingMs <= 0) return 'Expired';

    const totalSeconds = Math.floor(remainingMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s remaining`;
    } else {
      return `${seconds}s remaining`;
    }
  }, [attempt.started_at, attempt.past_papers.time_allowed]);

  const handleContinue = () => {
    navigate(`/paper-attempts/${attempt.id}`);
  };

  const handleExit = useCallback(async () => {
    try {
      await deletePaperAttempt.mutateAsync({ attemptId: attempt.id });
      setShowExitModal(false);
      // Invalidate the in-progress attempt query so the banner disappears
      queryClient.invalidateQueries({ queryKey: ['in_progress_paper_attempt', attempt.auth_id] });
    } catch (error) {
      console.error('Failed to delete paper attempt:', error);
    }
  }, [attempt.id, attempt.auth_id, deletePaperAttempt, queryClient]);

  return (
    <div className="bg-orange-50 border-2 border-orange-400 rounded-2xl p-6 flex items-center justify-between gap-4 shadow-sm">
      <div className="flex items-start gap-4 flex-1">
        <ExclamationCircleIcon className="w-8 h-8 text-orange-600 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-orange-900 mb-1">
            You have an in-progress paper
          </h3>
          <p className="text-orange-800 mb-2">
            <span className="font-medium">{attempt.past_papers.title}</span>
          </p>
          <div className="flex items-center gap-2 text-orange-700">
            <ClockIcon className="w-5 h-5" />
            <span className="font-medium">{timeRemaining}</span>
          </div>
        </div>
      </div>
      <div className="flex gap-3 flex-shrink-0">
        <Button
          size="lg"
          className="bg-red-500 hover:bg-red-600 border-none text-white px-8"
          onClick={() => setShowExitModal(true)}
        >
          Exit This Paper
        </Button>
        <Button
          size="lg"
          className="bg-orange-600 hover:bg-orange-700 border-none text-white px-8"
          onClick={handleContinue}
        >
          Continue Paper
        </Button>
      </div>

      {/* Exit Confirmation Modal */}
      <Modal open={showExitModal} onClose={() => setShowExitModal(false)} className="max-w-md">
        <Modal.Header className="font-bold text-xl">Exit Past Paper?</Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to exit? All your progress will be lost and this attempt will be deleted. You will
            not be able to resume later.
          </p>
        </Modal.Body>
        <Modal.Actions className="flex gap-3">
          <button
            onClick={() => setShowExitModal(false)}
            className="flex-1 bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-medium hover:bg-gray-50"
            disabled={deletePaperAttempt.isPending}
          >
            Cancel
          </button>
          <LoadingButton
            loading={deletePaperAttempt.isPending}
            onClick={handleExit}
            className="flex-1 bg-red-500 hover:bg-red-600 border-none text-white px-6 py-3 rounded-xl font-medium"
          >
            Exit & Delete
          </LoadingButton>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default InProgressBanner;
